/**
 * 계약 및 청구 주체 정보 상세 컴포넌트
 * 계약 및 청구 주체의 상세 정보를 표시하는 컴포넌트입니다.
 * 구분에 따라 해당없음/사업자/개인의 다른 정보를 표시합니다.
 */

import { useCallback, useEffect, useState } from "react";
import { DetailTotalData } from "src/api/contract/contract-types";
import { BaseTooltip } from "src/components";
import { formatPhoneNumber } from "src/utils";
import BaseSection from "../../../../components/layout/BaseSection";
import ContractImageUpload from "../ContractImageUpload";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import { PagePath } from "src/pages/product/details";
import { BizLocationType } from "src/pages/organization/org-types";

/**
 * 컴포넌트 props 인터페이스
 */
interface ContractBillingSubjectDetailProp {
  detailData: DetailTotalData; // 상품 상세 조회 정보
}

/**
 * 계약 및 청구 주체 정보 상세 컴포넌트
 * @param detailData 계약 상세 데이터
 */
const ContractBillingSubjectDetail = ({ detailData }: ContractBillingSubjectDetailProp) => {
  const { contractManage, contractOrganization } = detailData;
  const [classification, setClassification] = useState<"해당없음" | "개인" | "사업자">("해당없음"); // 구분 상태
  const [organization, setOrganization] = useState<any>(); // 조직 정보 상태
  const [individualInfo, setIndividualInfo] = useState<any>(); // 개인 정보 상태

  /**
   * 데이터 초기화 함수
   * 계약 관리 정보를 기반으로 구분을 설정하고 해당 정보를 로드합니다.
   */
  const initDatas = useCallback(() => {
    if (contractManage?.mbOrganizationId === "0" || !contractManage?.mbOrganizationId) {
      setClassification("해당없음");
    } else {
      // classifications 확인하여 개인/사업자 판별
      if (contractOrganization?.organization?.classifications && contractOrganization?.organization?.classifications.includes("INDIVIDUAL")) {
        setClassification("개인");
        setIndividualInfo(contractOrganization);
      } else {
        setClassification("사업자");
        setOrganization(contractOrganization);
      }
    }
  }, [contractManage, contractOrganization]);

  // 컴포넌트 마운트 시 데이터 초기화
  useEffect(() => {
    initDatas();
  }, [initDatas]);

  return (
    <>
      <div className="flex-center">
        <BaseSection
          titleChildren={
            <div className="flex-center">
              <p className="">구분</p>
              <BaseTooltip touchIcon={"QUESTION"} className="ml4 mt1">
                <ul className="base-list">
                  <li style={{ color: "#191919" }}>세금계산서 발행 시 '계약자' 메뉴에서 사업자 정보 등록은 필수입니다.</li>
                  <li style={{ color: "#191919" }}>현금영수증 발행 시 '계약자' 메뉴에서 개인 정보 등록은 필수입니다.</li>
                </ul>
              </BaseTooltip>
            </div>
          }
          children={
            <div className="flex-row flex-center-start">
              <span>{classification}</span>
            </div>
          }
        />
        {classification === "개인" && (
          <BaseSection
            title={"이름"}
            children={
              <div className="flex-row flex-center-start">
                <span>{individualInfo?.organization?.ceoName || "-"}</span>
                {individualInfo?.organization?.id && (
                  <p className="ml4 flex-row flex-center-start">
                    (id:
                    <BaseNewTabLink
                      path={PagePath.organization.detail.replace(":id", individualInfo?.organization?.id?.toString() || "")}
                      value={individualInfo?.organization?.id?.toString() || "-"}
                      ellipsis
                      className="ml4 mr4"
                    />
                    )
                  </p>
                )}
              </div>
            }
          />
        )}
        {classification === "사업자" && (
          <BaseSection
            title={"계약자(법인명/상호)"}
            children={
              <div className="flex-row flex-center-start">
                <span>{organization?.organization?.name || "-"}</span>
                {organization?.organization?.id && (
                  <p className="ml4 flex-row flex-center-start">
                    (계약자 ID:
                    <BaseNewTabLink
                      path={PagePath.organization.detail.replace(":id", organization?.organization?.id?.toString() || "")}
                      value={organization?.organization?.id?.toString() || "-"}
                      ellipsis
                      className="ml4 mr4"
                    />
                    )
                  </p>
                )}
              </div>
            }
          />
        )}
      </div>

      {/* 개인 선택 시 */}
      {classification === "개인" && (
        <>
          <div className="flex-center">
            <BaseSection title={"국가"} value={individualInfo?.organization?.bizLocationType === BizLocationType.OVERSEAS ? "해외" : "국내"} />
            <BaseSection title={"주소"} value={individualInfo?.organization?.address || "-"} />
          </div>
          <div className="flex-center">
            <BaseSection title={"휴대폰"} value={formatPhoneNumber(individualInfo?.organization?.phoneNumber || "") || "-"} />
            <BaseSection title={"이메일"} value={individualInfo?.organization?.email || "-"} />
          </div>
          <div className="flex-start">
            <BaseSection title={"증빙 서류 첨부"} children={<ContractImageUpload isOnlyView images={individualInfo?.mediaList} type={"PROOF"} />} />
          </div>
        </>
      )}

      {/* 사업자 선택 시 */}
      {classification === "사업자" && (
        <>
          <div className="flex-center">
            <BaseSection title={"국가"} value={organization?.organization?.bizLocationType === BizLocationType.OVERSEAS ? "해외" : "국내"} />
            <BaseSection
              title={"사업자 유형"}
              value={organization?.organization?.bizType ? (organization?.organization?.bizType === "CORP" ? "법인사업자" : "일반사업자") : "-"}
            />
          </div>
          <div className="flex-center">
            <BaseSection title={"대표자/성명"} value={organization?.organization?.ceoName || "-"} />
            <BaseSection title={"사업자 등록번호"} value={organization?.organization?.bizRegNo || "-"} />
          </div>
          <div className="flex-center">

            <BaseSection title={"사업장 주소"} value={organization?.organization?.address || "-"} />
          </div>

          <div className="flex-center">
            <BaseSection title={"사업자 등록증 첨부"} children={<ContractImageUpload isOnlyView images={organization?.mediaList} type={"PROOF"} />} />
          </div>
        </>
      )}
    </>
  );
};

export default ContractBillingSubjectDetail;
